import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, publicApi, API } from "@/lib/api";
import { BRAND, STATUS_META } from "@/lib/constants";
import TrackMap from "@/components/TrackMap";
import TripChat from "@/components/TripChat";
import PaymentSelector from "@/components/PaymentSelector";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Share2, RefreshCcw, CreditCard, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatPickupLocal } from "@/lib/datetime";

export default function Track() {
  const [params] = useSearchParams();
  const code = params.get("code");
  // Accept either ?email=… (legacy SMS/email links) or ?contact=… (new
  // Lookup-page deep-view links) — whichever is present is forwarded.
  const contact = params.get("contact") || params.get("email") || "";
  const email = contact;  // kept as alias for downstream PDF links that still expect 'email'
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [err, setErr] = useState(null);
  const [showPay, setShowPay] = useState(false);

  const refresh = async () => {
    if (!code || !contact) { setErr("Missing tracking code or contact"); return; }
    try {
      const r = await publicApi.get(`/track`, { params: { code, contact } });
      setData(r.data);
      const t = await publicApi.get(`/bookings/${r.data.booking.booking_id}/timeline`, { params: { code, contact } });
      setTimeline(t.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load tracking data");
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, contact]);

  const share = async () => {
    try {
      const url = window.location.href;
      if (navigator.share) {
        await navigator.share({ url, title: "Track my chauffeur", text: "Live ride tracking" });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch { /* ignore */ }
  };

  if (err) {
    return (
      <div className="public-light min-h-screen flex items-center justify-center bg-[#050505] text-[#F5F2EB] px-6">
        <div className="text-center">
          <div className="overline mb-3">Unable to load</div>
          <h2 className="font-serif text-3xl mb-3">{err}</h2>
          <a href="/lookup" className="btn-gold mt-6 inline-flex">Find reservation</a>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="public-light min-h-screen flex items-center justify-center bg-[#050505] text-[#A1A1AA]"><div className="overline">Locating your chauffeur…</div></div>;
  }

  const { booking, pickup_point, dropoff_point, driver_location, eta_minutes } = data;
  const meta = STATUS_META[booking.status] || STATUS_META.new;

  return (
    <div data-testid="track-page" className="public-light bg-[#050505] min-h-screen text-[#F5F2EB]">
      <header className="sticky top-0 z-40 bg-black/95 backdrop-blur border-b border-white/5">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <div className="overline">{booking.confirmation_code}</div>
            <div className="font-serif text-xl">{booking.vehicle_name}</div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="track-refresh" onClick={refresh} className="w-10 h-10 border border-white/10 flex items-center justify-center text-[#A1A1AA] hover:text-white"><RefreshCcw size={14}/></button>
            <button data-testid="track-share" onClick={share} className="w-10 h-10 border border-white/10 flex items-center justify-center text-[#D4AF37]"><Share2 size={14}/></button>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-5 pt-8 pb-12">
        {/* Status hero */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Current status</div>
            <span className="status-pill" style={{ color: meta.color }}>{meta.label}</span>
          </div>
          <h1 className="font-[Outfit] text-5xl sm:text-6xl tracking-tighter font-light leading-tight">
            {statusHeadline(booking.status, eta_minutes, driver_location, booking.assigned_driver_name)}
          </h1>
          {eta_minutes != null && booking.status === "en_route" && (
            <div className="mt-4 inline-flex items-center gap-2 border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-4 py-2">
              <Clock size={14} className="text-[#D4AF37]"/>
              <span className="text-sm">ETA <span className="text-[#D4AF37] font-serif text-lg">{eta_minutes}</span> minutes</span>
            </div>
          )}
          {/* Meet & Greet — driver has arrived at baggage claim / lobby */}
          {booking.meet_and_greet && booking.at_baggage_claim_at && (
            <div className="mt-4 border-2 border-[#D4AF37] bg-[#D4AF37]/10 px-5 py-4" data-testid="baggage-claim-badge">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-bold mb-1">✋ Your chauffeur is waiting</div>
              <div className="text-sm text-[#F5F2EB]">
                {booking.assigned_driver_name || "Your chauffeur"} is at baggage claim with a name sign for <span className="text-[#D4AF37] font-medium">{booking.customer_name}</span>. Look for the DELUX LIMO sign.
              </div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#71717A] mt-2">
                Notified at {new Date(booking.at_baggage_claim_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
          )}
          {/* Trip Manifest — passenger can pull up an officially-approved PDF
              with company verification + driver/vehicle details if pulled over. */}
          {booking.assigned_driver_name && (
            <div className="mt-4">
              <a
                data-testid="track-manifest-btn"
                href={`${API}/track/manifest.pdf?code=${encodeURIComponent(code)}&email=${encodeURIComponent(email)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black text-[11px] uppercase tracking-[0.22em] font-medium transition-colors"
              >
                <ShieldCheck size={14}/> Trip Manifest <span className="text-[9px] opacity-70">· law-enforcement ready</span>
              </a>
            </div>
          )}
        </div>

        {/* Map */}
        <TrackMap pickup={pickup_point} dropoff={dropoff_point} driver={driver_location}/>

        {/* Trip details + driver */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          <div className="md:col-span-2 border border-white/10 bg-[#0A0A0A] p-6 space-y-5">
            <div>
              <div className="overline mb-2">Pickup</div>
              <div className="text-[#F5F2EB]">{booking.pickup_address}</div>
              <div className="text-xs text-[#A1A1AA] mt-1" data-testid="track-pickup-time">{formatPickupLocal(booking.pickup_datetime)}</div>
            </div>
            {booking.dropoff_address && (
              <div>
                <div className="overline mb-2">Drop-off</div>
                <div className="text-[#F5F2EB]">{booking.dropoff_address}</div>
              </div>
            )}
            {booking.flight_number && (
              <div>
                <div className="overline mb-2">Flight</div>
                <div className="text-[#F5F2EB]">{booking.flight_number}</div>
              </div>
            )}
            <div>
              <div className="overline mb-2">{booking.guest_name ? "Booked by" : "Passenger"}</div>
              <div className="text-[#F5F2EB]">{booking.customer_name}</div>
            </div>
            {booking.guest_name && (
              <div data-testid="track-guest-info">
                <div className="overline mb-2 text-[#D4AF37]">Guest{booking.guest_count > 1 ? `s (${booking.guest_count})` : ""}</div>
                <div className="text-[#F5F2EB]">{booking.guest_name}</div>
                {booking.guest_phone && (
                  <div className="text-xs text-[#A1A1AA] mt-0.5">{booking.guest_phone}</div>
                )}
              </div>
            )}
          </div>

          <div className="border border-white/10 bg-[#0A0A0A] p-6 space-y-4">
            <div className="overline">Your chauffeur</div>
            {booking.assigned_driver_name ? (
              <>
                <div className="flex items-center gap-3">
                  {booking.assigned_driver_photo_url && (
                    <img
                      src={`${API.replace(/\/api$/, "")}${booking.assigned_driver_photo_url}`}
                      alt=""
                      className="w-14 h-14 rounded-full object-cover border-2 border-[#D4AF37]/50"
                      data-testid="track-driver-photo"
                    />
                  )}
                  <div className="font-serif text-xl text-[#D4AF37]">{booking.assigned_driver_name}</div>
                </div>
                {(() => {
                  const veh = [
                    booking.assigned_driver_vehicle_year,
                    booking.assigned_driver_vehicle_color,
                    booking.assigned_driver_vehicle_make,
                    booking.assigned_driver_vehicle_model,
                  ].filter(Boolean).join(" ");
                  const plate = booking.assigned_driver_license_plate || "";
                  const photo = booking.assigned_driver_vehicle_photo_available;
                  if (!veh && !plate && !photo) return null;
                  const photoUrl = photo
                    ? `${API}/track/photo?code=${encodeURIComponent(code)}&email=${encodeURIComponent(email)}`
                    : null;
                  return (
                    <div data-testid="track-vehicle-info" className="space-y-2 border-l-2 border-[#D4AF37]/40 pl-3">
                      {photoUrl && (
                        <a
                          href={photoUrl}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="track-vehicle-photo"
                          className="block border border-white/10 bg-black/30 overflow-hidden hover:border-[#D4AF37]/60 transition-colors"
                          title="Tap to view full size"
                        >
                          <img
                            src={photoUrl}
                            alt={`Your ${veh || "chauffeur vehicle"}`}
                            className="w-full h-32 object-cover"
                            loading="lazy"
                          />
                        </a>
                      )}
                      {veh && (
                        <div className="text-sm text-[#F5F2EB]">{veh}</div>
                      )}
                      {plate && (
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">
                          Plate · <span className="text-[#D4AF37] font-mono tracking-widest">{plate}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <p className="text-xs text-[#A1A1AA]">Licensed, background-checked. Tap to call once dispatch shares contact.</p>
                <a href={`tel:${BRAND.phones[0].replace(/[^0-9+]/g, "")}`} className="btn-ghost !py-2 !px-3 text-[10px] w-full"><Phone size={12}/>Call dispatch</a>
              </>
            ) : (
              <>
                <p className="text-sm text-[#A1A1AA]">Dispatch will assign your chauffeur shortly.</p>
                <a href={`tel:${BRAND.phones[0].replace(/[^0-9+]/g, "")}`} className="btn-ghost !py-2 !px-3 text-[10px] w-full"><Phone size={12}/>{BRAND.phones[0]}</a>
              </>
            )}
            {!booking.hide_price && booking.fare != null && (
            <div className="pt-3 border-t border-white/5">
              <div className="overline mb-1">Total</div>
              <div className="font-serif text-2xl text-[#D4AF37]">${booking.fare.toFixed(2)}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">{booking.payment_status === "paid" ? "Paid" : booking.payment_status === "balance_due" ? "Partially paid · balance due" : booking.payment_status === "pending" ? "Pending confirmation" : "Pay later"}</div>
              {booking.payment_status !== "paid" && booking.payment_status !== "pending" && (
                <button
                  data-testid="track-pay-now"
                  onClick={() => setShowPay(true)}
                  className="btn-gold !py-2 !px-4 text-[10px] w-full justify-center mt-3"
                ><CreditCard size={12}/>Add card · Pay now</button>
              )}
            </div>
            )}
          </div>
        </div>

        {/* Trip chat — only visible while trip is active */}
        {["assigned", "en_route", "in_progress"].includes(booking.status) && (
          <div className="mt-10">
            <TripChat
              bookingId={booking.booking_id}
              auth={{ code, email }}
              viewerHint="You · Customer"
            />
          </div>
        )}

        {/* Pickup grace policy — shown to the customer so they know
            how much complimentary wait time the chauffeur provides. */}
        {booking.wait_grace_minutes != null && (
          <div className="mt-10 border border-white/10 bg-[#0A0A0A] p-5 flex items-center gap-4" data-testid="track-grace-policy">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A] shrink-0">Pickup grace</div>
            <div className="flex-1">
              <span className="text-[#D4AF37] font-serif text-xl">{booking.wait_grace_minutes} min</span>
              <span className="text-xs text-[#A1A1AA] ml-2">complimentary wait — {booking.wait_grace_category}</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#71717A] hidden md:block">
              measured from your scheduled pickup
            </div>
          </div>
        )}

        {/* Timeline */}
        {(() => {
          // Hide audit/edit events from the customer-facing timeline — they
          // pollute the view (e.g. "edited 09:16 PM · edited 09:18 PM"). Also
          // hide dispatcher-only events like `pricing_updated`, `recalculated`,
          // `misc_charge_*` etc. so customers only see real trip progress.
          const DISPATCHER_ONLY = new Set([
            "pricing_updated", "pricing_recalculated", "recalculated",
            "fare_overridden", "misc_charge_added", "misc_charge_removed",
            "tidy", "modernized", "wait_grace_override",
            "confirmation_resent",
            // Driver link-click telemetry — internal dispatcher only
            "driver_trip_link_opened", "driver_calendar_link_opened",
            "driver_manifest_link_opened", "driver_auto_accepted_via_link",
          ]);
          const visible = timeline.filter(e => {
            const ev = String(e.event || "").toLowerCase();
            if (!ev) return false;
            if (ev === "edited" || ev.startsWith("edited") || ev === "booking_edited") return false;
            if (ev.startsWith("note_") || ev.startsWith("internal")) return false;
            if (ev.startsWith("communication_sent")) return false;
            if (DISPATCHER_ONLY.has(ev)) return false;
            // Catch-all for future driver_*_link_opened events
            if (ev.startsWith("driver_") && ev.endsWith("_link_opened")) return false;
            return true;
          });
          if (visible.length === 0) return null;
          return (
            <div className="mt-10 border border-white/10 bg-[#0A0A0A] p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="overline">Trip timeline</div>
              </div>
              <ol className="space-y-3 text-sm">
                {visible.map((e, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] mt-2 shrink-0"/>
                    <div className="flex-1">
                      <div className="text-[#F5F2EB]">{prettyEvent(e.event)}</div>
                      {e.driver_name && <div className="text-xs text-[#A1A1AA]">Driver: {e.driver_name}</div>}
                      {e.distance_miles != null && <div className="text-xs text-[#A1A1AA]">{(e.distance_miles * 5280).toFixed(0)} ft from pickup</div>}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">{new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  </li>
                ))}
              </ol>
            </div>
          );
        })()}

        <div className="mt-10 text-center text-xs text-[#71717A]">
          Auto-refreshing every 15 seconds · Powered by Delux Limo dispatch
        </div>
      </section>

      {/* Pay-now modal — opens when the customer wants to add a card / pay
          after initially picking "pay later". */}
      {showPay && (
        <div
          data-testid="track-pay-modal"
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowPay(false)}
        >
          <div
            className="bg-[#050505] border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="track-pay-close"
              onClick={() => setShowPay(false)}
              className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white"
              aria-label="Close"
            ><X size={18}/></button>
            <div className="overline mb-1">Pay for your ride</div>
            <div className="font-serif text-2xl text-[#D4AF37] mb-4">{booking.confirmation_code}</div>
            <PaymentSelector
              booking={{ ...booking, customer_email: email }}
              onPaid={() => { setShowPay(false); refresh(); toast.success("Payment updated"); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function statusHeadline(status, eta, driver, name) {
  switch (status) {
    case "new":         return "Your ride is being prepared.";
    case "assigned":    return `${name || "Your chauffeur"} is on standby.`;
    case "en_route":    return driver ? "Your chauffeur is on the way." : `${name || "Your chauffeur"} is en route.`;
    case "in_progress": return "Enjoy the ride.";
    case "completed":   return "Thank you for riding with Delux.";
    case "cancelled":   return "This reservation was cancelled.";
    default:            return "Tracking your ride.";
  }
}

function prettyEvent(ev) {
  const map = {
    "status:assigned":    "Chauffeur assigned",
    "status:en_route":    "Chauffeur en route to pickup",
    "status:in_progress": "Trip started",
    "status:completed":   "Trip completed",
    "status:cancelled":   "Trip cancelled",
    "geofence_arrival":   "Chauffeur arrived at pickup (auto-detected)",
    "at_baggage_claim":   "Chauffeur waiting at baggage claim",
  };
  return map[ev] || ev.replace("status:", "").replace(/_/g, " ");
}
